import { localizedStrings } from '@core/strings'
import { Card, CardProps } from '@common/material'
import React from 'react'
import { Box, Flex, Text } from '@fundamentals'
import { Section } from '@common/Section'
import moment from 'moment'
import { TaskTemplateProperties } from '@core/entities/task/TaskTemplateProperties'
import EventAvailableOutlined from '@mui/icons-material/EventAvailableOutlined'
import EventBusy from '@mui/icons-material/EventBusy'
import Person from '@mui/icons-material/Person'
import Flag from '@mui/icons-material/Flag'
import LocationOnOutlined from '@mui/icons-material/LocationOnOutlined'
import { TaskStatusChip } from '@modules/tasks/components/TaskStatusChip'
import { LinkedTaskResult } from '@core/entities/linkedProcesses'

type TaskCardProps = Omit<CardProps, 'id'> & {
  task: LinkedTaskResult
  icon?: React.ReactNode
}

export const TaskCard: React.FC<TaskCardProps> = ({
  task,
  icon,
  sx = {},
  children,
  ...props
}) => {
  return (
    <Card
      sx={{
        pl: 2,
        pr: icon ? 1.5 : 2,
        py: 1.5,
        ...sx,
      }}
      {...props}
    >
      <Flex>
        <Flex flex={1} flexDirection='column' gap={1}>
          <Text fontSize={13} color='text.secondary'>
            {task?.template?.name}
          </Text>
          <Text fontSize={15}>{task?.summary}</Text>

          <TaskStatusChip
            status={task?.status}
            label={task?.currentStep?.name}
            hasRequestedChanges={task?.hasRequestedChanges?.()}
          />

          <Section
            label={localizedStrings.assignTo}
            icon={<Person sx={{ fontSize: 16, color: 'grey' }} />}
            horizontal
          >
            <Text fontSize={14}>{task?.assignee?.getFullName?.()}</Text>
          </Section>

          <Section
            label={localizedStrings.priority}
            icon={<Flag sx={{ fontSize: 16, color: 'grey' }} />}
            horizontal
          >
            <Flex alignItems='center'>
              <Box
                height={12}
                width={12}
                borderRadius={50}
                sx={{
                  backgroundColor: TaskTemplateProperties.getColorForPriority(
                    task?.priority,
                  ),
                  mr: 0.5,
                }}
              />
              <Text fontSize={14} sx={{ textTransform: 'capitalize' }}>
                {task?.priority?.toLowerCase()}
              </Text>
            </Flex>
          </Section>

          {!!task?.location && (
            <Section
              label={localizedStrings.location}
              icon={<LocationOnOutlined sx={{ fontSize: 16, color: 'grey' }} />}
              horizontal
            >
              <Text fontSize={14}>{task.location}</Text>
            </Section>
          )}

          <Section
            label={localizedStrings.dueDate}
            labelColor={task?.isOverdue?.() ? 'error.main' : 'text.disabled'}
            icon={
              <EventBusy
                sx={{
                  fontSize: 16,
                  color: task?.isOverdue?.() ? 'error.main' : 'text.disabled',
                }}
              />
            }
            horizontal
          >
            <Text
              fontSize={14}
              sx={{
                color: task?.isOverdue?.() ? 'error.main' : 'text.primary',
              }}
            >
              {task?.dueDate
                ? moment(task?.dueDate).format('DD MMM YYYY HH:mm')
                : '-'}
            </Text>
          </Section>

          {!!task?.createdOn && (
            <Section
              label={localizedStrings.created}
              icon={
                <EventAvailableOutlined sx={{ fontSize: 16, color: 'grey' }} />
              }
              horizontal
            >
              <Text sx={{ fontSize: 14 }}>
                {moment(task?.createdOn).format('DD MMM YYYY HH:mm')}
              </Text>
            </Section>
          )}
        </Flex>

        {icon && <Box>{icon}</Box>}
      </Flex>
      {children}
    </Card>
  )
}
